import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Card,
  Button,
  Typography,
  TextField,
  InputAdornment,
  capitalize,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Search, AccountCircle, Delete } from "@material-ui/icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

import { BaseUrl, ImageBaseUrl } from "../../../Environment";
import { getSecureItem } from "../../../utilsFunctions";
import Sidebar from "../../sidebar/sidebar";
import companylogo from "../../../assets/images/companylogo.svg";
import { Stack } from "@mui/material";
import ImageZoomModal from "../../modals/ImageZoomModal";

const TOAST_OPTS = {
  position: "top-center",
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: false,
  pauseOnHover: false,
  draggable: false,
};

const COLOR_PAID = "#00d95e";
const COLOR_MID = "#3f6fea";
const COLOR_LOW = "#b0b6c3";
const COLOR_WHITE = "#fff";
const FONT_FAMILY = '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif';

const STATUS_TABS = [
  { key: "Open", label: "Open" },
  { key: "Closed", label: "Closed" },
  { key: "Cancelled", label: "Cancelled" },
];

const PAYMENT_TABS = [
  { key: "Due", label: "Due" },
  { key: "Overdue", label: "Overdue" },
];

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: FONT_FAMILY,
    "& *": {
      fontFamily: "inherit",
    },
  },
  wrapper: {
    margin: "10px 40px 0px 110px",
  },
  headerBar: {
    marginBottom: theme.spacing(3),
  },
  headerLogo: {
    height: 40,
  },
  searchInput: {
    width: 430,
    "& .MuiInputBase-root": {
      background: "#EFF2FF",
      borderRadius: 10,
      height: 40,
      padding: "0 15px",
    },
  },
  toolbarRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    height: 50,
  },
  toolbarTabs: {
    justifySelf: "center",
  },
  toolbarAction: {
    justifySelf: "end",
  },
  paymentTabRow: {
    marginBottom: theme.spacing(2.5),
  },
  addButton: {
    background: COLOR_PAID,
    color: "#fff",
    textTransform: "none",
    padding: theme.spacing(1, 3),
    borderRadius: 8,
    "&:hover": {
      background: "#00b84f",
    },
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(3),
  },
  card: {
    position: "relative",
    padding: theme.spacing(2.5),
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    cursor: "pointer",
    transition: "transform 0.2s ease, border-color 0.2s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      background: "linear-gradient(135deg, #00d95e, #4277db)",
      "& $customerName": { color: "#fff" },
      "& $subName": { color: "#fff" },
      "& $customerMeta": { color: "#fff" },
      "& $amountValue": { color: "#fff" },
      "& $schemeAgentLine": { color: "#fff" },
      "& $footerValue": { color: "#fff" },
      "& $footerLabel": { color: "#fff" },
      "& $deleteIcon": { color: COLOR_WHITE },
      border: "none",
    },
  },
  customerRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  avatarIcon: {
    fontSize: 42,
    color: "#c7cbd6",
  },
  customerName: {
    fontWeight: 600,
    fontSize: "17px",
    color: "#40486d",
  },
  subName: {
    fontWeight: 400,
    fontSize: "12px",
    color: "#8e8e98",
  },
  customerMeta: {
    display: "flex",
    flexDirection: "column",
    fontSize: "0.8rem",
    color: "#8e8e98",
    minWidth: 0,
  },
  deleteIcon: {
    color: COLOR_LOW,
    cursor: "pointer",
    transition: "color 0.2s ease",
  },
  amountRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  amountBlock: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
  },
  amountValue: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#757575",
  },
  statusTagRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    margin: theme.spacing(0.5, 0),
  },
  dueStatusTag: {
    background: "#fff9e5",
    color: "#ffa500",
    fontSize: "0.72rem",
    fontWeight: 600,
    padding: theme.spacing(0.4, 1.25),
    borderRadius: 12,
    textTransform: "uppercase",
  },
  overdueStatusTag: {
    background: "#ffe5e5",
    color: "#ff0000",
    fontSize: "0.72rem",
    fontWeight: 600,
    padding: theme.spacing(0.4, 1.25),
    borderRadius: 12,
    textTransform: "uppercase",
  },
  tenureTag: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#6C63FF",
  },
  schemeAgentLine: {
    fontSize: "0.8rem",
    color: "#8e8e98",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  footerRow: {
    display: "flex",
    justifyContent: "space-between",
    borderTop: "1px solid #eef0f5",
    paddingTop: theme.spacing(1.5),
  },
  footerValue: {
    fontWeight: 600,
    color: "#40486d",
    fontSize: "14px",
  },
  footerLabel: {
    fontSize: "0.75rem",
    color: "#8e8e98",
  },
  emptyState: {
    textAlign: "center",
    marginTop: theme.spacing(5),
  },
}));

const useTabStyles = makeStyles((theme) => ({
  tabBar: {
    display: "flex",
    gap: theme.spacing(1),
  },
  option: {
    fontSize: "14px",
    fontWeight: 600,
    padding: theme.spacing(0.75, 2),
    borderRadius: 8,
    cursor: "pointer",
    color: "#8e8e98",
    background: "transparent",
    transition: "background 0.2s ease, color 0.2s ease",
    userSelect: "none",
    "&:hover": {
      background: "#EFF2FF",
      color: "#40486d",
    },
  },
  optionActive: {
    background: "#EFF2FF",
    color: "#4d66f0",
    "&:hover": {
      background: "#EFF2FF",
      color: "#4d66f0",
    },
  },
  countSpan: {
    marginLeft: "5px",
    fontWeight: 600,
  },
}));

const formatDate = (isoStr) => {
  if (!isoStr) return "-";
  return new Date(isoStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** Small circular progress ring, drawn as SVG — no chart library dependency. */
const PercentRing = ({ percent = 0 }) => {
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(100, Math.max(0, percent));
  const offset = circumference - (clamped / 100) * circumference;
  const color =
    clamped >= 75 ? COLOR_PAID : clamped >= 25 ? COLOR_MID : COLOR_LOW;

  return (
    <svg width="64" height="64" viewBox="0 0 64 64">
      <circle
        cx="32"
        cy="32"
        r={radius}
        fill="none"
        stroke="#e6e9f2"
        strokeWidth="5"
      />
      <circle
        cx="32"
        cy="32"
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="5"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 32 32)"
        style={{ transition: "stroke-dashoffset 0.4s ease" }}
      />
      <text
        x="32"
        y="37"
        textAnchor="middle"
        fontSize="14"
        fontWeight="600"
        fill="#3a3a3a"
        className="Fonts"
      >
        {clamped}%
      </text>
    </svg>
  );
};

/** Reusable pill-style tab bar. `counts` is optional — omit a key to hide the number. */
const TabBar = ({ tabs, activeTab, onTabChange, counts }) => {
  const tabClasses = useTabStyles();

  return (
    <div className={tabClasses.tabBar}>
      {tabs.map((tab) => (
        <div
          key={tab.key}
          role="tab"
          aria-selected={activeTab === tab.key}
          onClick={() => onTabChange(tab.key)}
          className={`${tabClasses.option} Fonts btnSizeFont ${
            activeTab === tab.key ? tabClasses.optionActive : ""
          }`}
        >
          <span>{tab.label}</span>
          {counts && (
            <span className={tabClasses.countSpan}>
              ({counts?.[tab.key] ?? 0})
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const GetAllSavings = () => {
  const classes = useStyles();
  const history = useHistory();

  const [allSavings, setAllSavings] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [activeStatusTab, setActiveStatusTab] = useState("Open");
  const [activePaymentTab, setActivePaymentTab] = useState("Due");

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getSecureItem("token")}`,
  });

  const getAllSavings = async () => {
    setLoading(true);
    try {
      const loggedinUser = getSecureItem("loggedinUser");

      const resp = await axios.get(`${BaseUrl}/savings/getAllSavings`, {
        params: { companyId: loggedinUser?.companyId },
        headers: authHeaders(),
      });

      // percentCollected & nextDueDate now come pre-computed from the API.
      setAllSavings(resp.data?.data || []);
    } catch (err) {
      console.error("GetAllSavings Error:", err);
      toast.error("Unable to fetch savings!", TOAST_OPTS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllSavings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addSaving = () => history.push("/addsaving");
  const viewSaving = (savingId) => history.push(`/savings/${savingId}`);

  const handleDeleteSaving = async (e, saving) => {
    e.stopPropagation();

    const customerLabel = saving.customer
      ? `${saving.customer.firstName} ${saving.customer.lastName}`
      : "this saving";

    const confirmed = window.confirm(
      `Are you sure you want to delete the saving for ${customerLabel}? This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingId(saving.savingId);
    try {
      await axios.delete(`${BaseUrl}/savings/deleteSaving/${saving.savingId}`, {
        headers: authHeaders(),
      });

      setAllSavings((prev) =>
        prev.filter((s) => s.savingId !== saving.savingId),
      );
      toast.success("Saving deleted successfully!", TOAST_OPTS);
    } catch (err) {
      console.error("Delete Saving Error:", err);
      toast.error(
        err?.response?.data?.message || "Unable to delete saving!",
        TOAST_OPTS,
      );
    } finally {
      setDeletingId(null);
    }
  };

  // Search-only match, shared by the list filter and both tab-count calculations
  // below so "how many are Due" always reflects the current search text.
  const matchesSearchText = (s) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();

    return [
      `${s.customer?.firstName || ""} ${s.customer?.lastName || ""}`,
      s.customer?.cust_id,
      s.schemeType?.schemeType,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(search));
  };

  const statusCounts = useMemo(() => {
    const counts = {};
    STATUS_TABS.forEach((tab) => {
      counts[tab.key] = allSavings.filter(
        (s) =>
          s.savingStatus === tab.key &&
          matchesSearchText(s),
      ).length;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSavings, activePaymentTab, searchText]);

  const paymentCounts = useMemo(() => {
    const counts = {};
    PAYMENT_TABS.forEach((tab) => {
      counts[tab.key] = allSavings.filter(
        (s) =>
          s.paymentStatus === tab.key &&
          s.savingStatus === activeStatusTab &&
          matchesSearchText(s),
      ).length;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSavings, activeStatusTab, searchText]);

  const filteredSavings = useMemo(() => {
    return allSavings.filter(
      (s) =>
        s.savingStatus === activeStatusTab &&
        s.paymentStatus === activePaymentTab &&
        matchesSearchText(s),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSavings, activeStatusTab, activePaymentTab, searchText]);

  return (
    <div className={classes.root}>
      <Sidebar />
      <div className={classes.wrapper}>
        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          marginBottom={3}
        >
          <img src={companylogo} className={classes.headerLogo} alt="logo" />
          <TextField
            className={classes.searchInput}
            placeholder="Search by customer name, id or savings type..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <div className={classes.toolbarRow}>
          <div />
          <div className={classes.toolbarTabs}>
            <TabBar
              tabs={STATUS_TABS}
              activeTab={activeStatusTab}
              onTabChange={setActiveStatusTab}
              counts={statusCounts}
            />
          </div>
          <div className={classes.toolbarAction}>
            <Button className={classes.addButton} onClick={addSaving}>
              + Add Saving
            </Button>
          </div>
        </div>

        <Stack
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          className={classes.paymentTabRow}
        >
          <TabBar
            tabs={PAYMENT_TABS}
            activeTab={activePaymentTab}
            onTabChange={setActivePaymentTab}
            counts={paymentCounts}
          />
        </Stack>

        {loading ? (
          <Typography className={`Fonts ${classes.emptyState}`}>
            Loading savings...
          </Typography>
        ) : filteredSavings.length === 0 ? (
          <Typography className={`Fonts ${classes.emptyState}`}>
            No savings found.
          </Typography>
        ) : (
          <div className={classes.grid}>
            {filteredSavings.map((saving) => (
              <Card
                className={classes.card}
                key={saving.savingId}
                onClick={() => viewSaving(saving.savingId)}
              >
                <div className={classes.customerRow} onClick={(e) => e.stopPropagation()}>
                  {saving?.customer?.proofs?.[0]?.proofType === "profilepic" ? (
                    <div style={{ width: 50, height: 50, borderRadius: 10, overflow: "hidden" }}>
                      <ImageZoomModal
                        src={`${ImageBaseUrl}${saving?.customer?.proofs?.[0]?.proofImagePath}`}
                        alt="Profile Picture"
                        size={50}
                        allowDownload
                        fileName={`${saving?.customer?.firstName || "profile"}.jpg`}
                      />
                    </div>
                  ) : (
                    <AccountCircle className={classes.avatarIcon} />
                  )}
                  <div className={classes.customerMeta}>
                    <Typography
                      noWrap
                      className={`Fonts ${classes.customerName}`}
                    >
                      {saving.customer
                        ? `${saving.customer.firstName} ${saving.customer.lastName} (${saving.customer.cust_id})`
                        : "-"}
                    </Typography>
                    <Typography noWrap className={`Fonts ${classes.subName}`}>
                      {saving.savingCode} | {saving.customer?.phone || "-"}
                    </Typography>
                  </div>
                  <div>
                    <Delete
                      className={classes.deleteIcon}
                      style={{
                        opacity: deletingId === saving.savingId ? 0.5 : 1,
                        pointerEvents:
                          deletingId === saving.savingId ? "none" : "auto",
                      }}
                      onClick={(e) => handleDeleteSaving(e, saving)}
                    />
                  </div>
                </div>

                <div className={classes.amountRow}>
                  <PercentRing percent={saving.percentCollected || 0} />
                  <div className={classes.amountBlock}>
                    <Typography className={`Fonts ${classes.amountValue}`}>
                      {saving.totalAmount}
                    </Typography>
                    <div className={classes.statusTagRow}>
                      <span
                        className={`Fonts ${
                          saving?.paymentStatus === "Due"
                            ? classes.dueStatusTag
                            : classes.overdueStatusTag
                        }`}
                      >
                        {saving?.paymentStatus}
                      </span>
                    </div>
                    <Typography className={`Fonts ${classes.schemeAgentLine}`}>
                      {saving.schemeType?.schemeType} |{" "}
                      {saving.user
                        ? `${saving.user.firstName} ${saving.user.lastName}`
                        : "-"}
                    </Typography>
                  </div>
                </div>

                <div className={classes.footerRow}>
                  <div>
                    <Typography className={`Fonts ${classes.footerValue}`}>
                      {formatDate(saving.nextDueDate)}
                    </Typography>
                    <Typography className={`Fonts ${classes.footerLabel}`}>
                      Due Date
                    </Typography>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <Typography className={`Fonts ${classes.footerValue}`}>
                      {capitalize(saving.tenureType)}
                    </Typography>
                    <Typography className={`Fonts ${classes.footerLabel}`}>
                      Collection Seq.
                    </Typography>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GetAllSavings;
